<i18n src="./resources/locales.json"></i18n>

<template>
  <v-dialog v-model="isSettingsModalOpen" persistent max-width="600px">
    <v-tabs-items v-if="isSettingsModalOpen" v-model="tab">
      <!-- List of slides tab -->
      <v-tab-item>
        <v-card color="surface lighten-1" class="main-card">
          <v-card-title primary-title>
            <div class="xtium-title">{{ $t('wallboard.cardConfiguration') }}</div>
          </v-card-title>

          <v-divider />
          <v-progress-linear v-show="isLoading" class="loading" height="2" color="primary" indeterminate />

          <div class="aic-config-body surface">
            <v-list subheader two-line flat color="surface" :rounded="false" over>
              <v-subheader class="group-subheader">{{ $t('wallboard.card') }}</v-subheader>
              <div class="px-4">
                <v-text-field v-model="localCardData.name" :label="$t('wallboard.cardName')"></v-text-field>
              </div>
              <!-- Card Size Selector -->

              <div v-if="!isSupervisor" class="px-4">
                <v-select
                  v-model="localCardData.sizeRatio"
                  :items="possibleCardSizes"
                  class="mt-5"
                  :label="$t('wallboard.size')"
                >
                </v-select>
              </div>

              <div v-else class="px-4">
                <p class="group-subheader">{{ $t('wallboard.size') }}</p>
                <div class="card-size">
                  <v-label class="size-value v-label" :dark="$vuetify.theme.dark">
                    {{ $t('wallboard.value') }}
                  </v-label>
                  <v-label class="size-type" :dark="$vuetify.theme.dark">{{ $t('wallboard.type') }}</v-label>
                  <v-text-field
                    v-model="width.value"
                    type="number"
                    min="10"
                    class="size-value"
                    required
                    :label="$t('wallboard.width')"
                    :disabled="isLoading"
                  />
                  <v-select v-model="width.type" :items="widthSizeTypes" class="size-type"> </v-select>
                  <v-text-field
                    v-model="height.value"
                    type="number"
                    min="10"
                    class="text-sm-left size-value"
                    required
                    :label="$t('wallboard.height')"
                    :disabled="isLoading"
                  />
                  <v-select v-model="height.type" :items="heightSizeTypes" class="mt-5 size-type"> </v-select>
                </div>
              </div>
            </v-list>

            <v-list v-if="!isSupervisorCard" subheader two-line flat color="surface" :rounded="false" over>
              <v-subheader class="group-subheader mt-0">
                {{ $t('wallboard.slides') }}
              </v-subheader>
              <v-list-item-group multiple>
                <Container @drop="onDrop">
                  <Draggable v-for="slide in localCardData.slides" :key="slide._id">
                    <v-list-item ref="cards" @click.stop="openSlideSettings(slide, true)">
                      <template>
                        <v-list-item-content>
                          <v-list-item-title>{{ slide.name }}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action v-if="localCardData.slides.length > 1">
                          <v-btn icon @click.stop="removeSlide(slide)">
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </v-list-item-action>
                      </template>
                    </v-list-item>
                  </Draggable>
                </Container>

                <!-- Add new Slide button -->
                <v-list-item @click="openSlideSettings(newSlideTemplate)">
                  <template>
                    <v-list-item-action>
                      <v-icon left>mdi-plus</v-icon>
                    </v-list-item-action>

                    <v-list-item-content>
                      <v-list-item-title>{{ $t('wallboard.addNewSlide') }}</v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </div>

          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-tooltip color="primary" top :disabled="isLoading || cardHasInvalidValues || cardHasNoChanges">
              <template v-slot:activator="{ on }">
                <v-btn text v-on="on" @click="closeModal()">
                  <span>{{ $t('wallboard.close') }}</span>
                </v-btn>
              </template>
              <span>{{ $t('wallboard.closeTooltip') }}</span>
            </v-tooltip>

            <v-btn
              :loading="isLoading"
              :disabled="isLoading || cardHasInvalidValues || cardHasNoChanges"
              color="primary"
              depressed
              @click="updateCard()"
            >
              <span v-once>
                {{ $t('wallboard.save') }}
              </span>
              <span v-once ref="saveSuccessful" class="alert">
                {{ $t('wallboard.saveSuccessful') }}
              </span>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-tab-item>

      <!-- Edit slide tab -->
      <v-tab-item>
        <v-card color="surface lighten-1">
          <v-card-title primary-title>
            <div v-once class="xtium-title">{{ $t('wallboard.slideSettings') }}</div>
          </v-card-title>

          <v-divider />

          <div class="aic-add-card-body surface pa-4">
            <div class="customization-form">
              <!-- Slide Name Input -->
              <v-text-field
                v-model="selectedSlide.name"
                :label="$t('wallboard.slideName')"
                hide-details="auto"
              ></v-text-field>

              <!-- Multiple Business Process Selector -->
              <v-select
                v-model="selectedSlide.businessProcesses"
                :items="availableBusinessProcesses"
                class="mt-5"
                multiple
                item-value="businessProcessId"
                item-text="businessProcessName"
                :label="$t('wallboard.businessProcesses')"
              >
                <template v-slot:prepend-item>
                  <v-list-item @click="toggleSelectAllBPs">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ $t('wallboard.selectAll') }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-divider class="mt-2"></v-divider>
                </template>
              </v-select>

              <!-- Graph Thresholds slider -->
              <div v-if="localCardData.componentName === 'EcsServiceLevelSlide'">
                <v-range-slider
                  v-model="selectedSlide.graphThreshold"
                  :label="$t('wallboard.slideGraphRange')"
                  :value="selectedSlide.graphThreshold"
                  class="graph-range-slider"
                  step="5"
                  thumb-label="always"
                  :max="100"
                  :min="0"
                >
                  <template v-slot:thumb-label="{ value }"> {{ value }}% </template>
                </v-range-slider>
              </div>

              <!-- Show As Graph toggler -->
              <v-subheader
                v-if="localCardData.componentName === 'EcsInteractionsSlide'"
                class="group-subheader pa-0 mt-0"
              >
                {{ $t('wallboard.graphTypeHeader') }}
              </v-subheader>

              <v-switch
                v-if="
                  localCardData.componentName === 'EcsInteractionsSlide' ||
                    localCardData.componentName === 'EcsAgentsStatesSlide' ||
                    localCardData.componentName === 'EcsCallbacksSlide'
                "
                v-model="selectedSlide.showAsGraph"
                color="primary"
                :label="$t('wallboard.showAsGraph') + graphType"
                class="mb-5"
                hide-details
              ></v-switch>

              <!-- Show Agent Names toggler -->

              <v-switch
                v-if="localCardData.componentName === 'EcsAgentsStatesSlide'"
                v-model="selectedSlide.showAgentNames"
                color="primary"
                :label="$t('wallboard.agentsName')"
                class="mb-5"
                hide-details
              ></v-switch>

              <!-- States Filter -->
              <div v-if="localCardData.componentName === 'EcsAgentsStatesSlide'">
                <v-subheader class="group-subheader pa-0 mt-0">{{ $t('wallboard.agentStateFilter') }}</v-subheader>
                <div class="filters-container">
                  <v-checkbox
                    v-for="state in possibleFilters.states"
                    :key="state"
                    v-model="selectedSlide.states"
                    :label="state"
                    :value="state"
                  ></v-checkbox>
                </div>
              </div>

              <!-- Callbacks Filter -->
              <div v-if="localCardData.componentName === 'EcsCallbacksSlide'">
                <v-subheader class="group-subheader pa-0 mt-0">{{ $t('wallboard.callbackTypeFilter') }}</v-subheader>
                <div class="filters-container">
                  <v-checkbox
                    v-for="callback in possibleFilters.callbacks"
                    :key="callback"
                    v-model="selectedSlide.callbacks"
                    :label="callback"
                    :value="callback"
                  ></v-checkbox>
                </div>
              </div>

              <!-- Interactions Filter -->
              <div v-if="localCardData.componentName === 'EcsInteractionsSlide'">
                <v-subheader class="group-subheader pa-0 mt-0">
                  {{ $t('wallboard.interactionStateFilter') }}
                </v-subheader>
                <div class="filters-container">
                  <v-checkbox
                    v-for="interaction in possibleFilters.interactionsTypeList"
                    :key="interaction"
                    v-model="selectedSlide.interactionsTypeList"
                    :label="interaction"
                    :value="interaction"
                    @change="interactionsUpdated(interaction)"
                  ></v-checkbox>
                </div>
              </div>

              <!-- Media types filter -->
              <div
                v-if="
                  localCardData.componentName === 'EcsInteractionsSlide' ||
                    localCardData.componentName === 'EcsAverageSpeedAnswerSlide' ||
                    localCardData.componentName === 'EcsLongestWaitingTimeSlide'
                "
              >
                <v-subheader class="group-subheader pa-0 mt-0">{{ $t('wallboard.mediaTypeFilter') }}</v-subheader>
                <div class="filters-container">
                  <v-checkbox
                    v-for="mediaType in possibleFilters.liveMediaTypes"
                    :key="mediaType"
                    v-model="selectedSlide.mediaTypes"
                    :label="mediaType"
                    :value="mediaType"
                  ></v-checkbox>
                </div>
              </div>
            </div>
          </div>

          <v-divider />
          <v-spacer />
          <v-card-actions class="px-0">
            <v-spacer />
            <v-btn text @click="discard">
              <span v-once>{{ $t('wallboard.close') }}</span>
            </v-btn>
            <v-btn
              v-if="isEditingMode"
              color="primary"
              :disabled="slideHasInvalidValues || slideHasNoChanges"
              depressed
              @click="editSlide"
            >
              <span v-once>{{ $t('wallboard.save') }}</span>
            </v-btn>
            <v-btn v-if="!isEditingMode" color="primary" depressed :disabled="slideHasInvalidValues" @click="addSlide">
              <span v-once>{{ $t('wallboard.addSlide') }}</span>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </v-dialog>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import { Container, Draggable } from 'vue-smooth-dnd';
import BusinessProcess from '@/classes/BusinessProcess';

export default {
  name: 'EcsWallboardSlideConfigDialog',
  components: { Container, Draggable },

  props: {
    dashboardId: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      initialCardData: null,
      localCardData: null,
      isLoading: false,
      tab: 0,
      isEditingMode: false,
      possibleCardSizes: ['Small', 'Medium', 'Large'],
      possibleFilters: {
        states: ['Ready', 'Break', 'Busy', 'Handle Outgoing', 'No Answer', 'Backoffice'],
        callbacks: ['Failed Percent', 'Processed', 'Purged', 'Reached Percent', 'Requested', 'To Be Proccesed'],
        interactionsTypeList: ['Overflow', 'Abandoned', 'Answered', 'Waiting', 'Queued'],
        mediaTypes: ['Telephony', 'Chat', 'SMS', 'E-mail', 'Fax'],
        liveMediaTypes: ['Telephony', 'Chat', 'SMS']
      },
      widthSizeTypes: ['px', '%', 'vw'],
      heightSizeTypes: ['px', '%', 'vh'],
      width: {},
      height: {},
      initialSlideData: { showAsGraph: false, showAgentNames: false, businessProcesses: [] },
      selectedSlide: { showAsGraph: false, showAgentNames: false, businessProcesses: [] },
      supervisorCards: ['EcsSupervisedAgentsSlide', 'EcsSupervisedInteractionsSlide'],
      isSupervisorCard: false
    };
  },

  computed: {
    ...mapGetters('AGENT', ['isSupervisor']),
    ...mapGetters('WALLBOARD', ['getWallboardCards', 'getWallboardStatistics']),

    availableBusinessProcesses() {
      return [...this.getWallboardStatistics].sort(BusinessProcess.compare);
    },

    availableBusinessProcessesIds() {
      return this.getWallboardStatistics.map(bp => bp.businessProcessId);
    },

    wallboardCards() {
      return this.getWallboardCards;
    },

    isSettingsModalOpen() {
      return !!this.localCardData;
    },

    newSlideTemplate() {
      const slide = {
        name: '',
        graphThreshold: [20, 80],
        businessProcesses: [],
        states: [],
        callbacks: [],
        interactionsTypeList: [],
        showAsGraph: true,
        mediaTypes: []
      };

      return slide;
    },

    cardHasInvalidValues() {
      return !this.localCardData.name || this.localCardData.name === '';
    },

    slideHasInvalidValues() {
      if (!this.selectedSlide.businessProcesses || this.selectedSlide.businessProcesses.length === 0) return true;

      if (this.localCardData.componentName === 'EcsAgentsStatesSlide') {
        if (!this.selectedSlide.states || this.selectedSlide.states.length === 0) {
          return true;
        }
      }

      if (this.localCardData.componentName === 'EcsCallbacksSlide') {
        if (!this.selectedSlide.callbacks || this.selectedSlide.callbacks.length === 0) {
          return true;
        }
      }

      if (this.localCardData.componentName === 'EcsInteractionsSlide') {
        if (!this.selectedSlide.interactionsTypeList || this.selectedSlide.interactionsTypeList.length === 0) {
          return true;
        }
      }

      if (
        this.localCardData.componentName === 'EcsInteractionsSlide' ||
        this.localCardData.componentName === 'EcsAverageSpeedAnswerSlide' ||
        this.localCardData.componentName === 'EcsLongestWaitingTimeSlide'
      ) {
        if (!this.selectedSlide.mediaTypes || this.selectedSlide.mediaTypes.length === 0) {
          return true;
        }
      }

      return false;
    },

    cardHasNoChanges() {
      if (this.localCardData.name !== this.initialCardData.name) return false;

      if (this.localCardData.sizeRatio !== this.initialCardData.sizeRatio) return false;

      if (this.initialCardData.customSizeRatio) {
        if (this.initialCardData.customSizeRatio.width) {
          if (this.initialCardData.customSizeRatio.width.value !== this.width.value) {
            return false;
          }

          if (this.initialCardData.customSizeRatio.width.type !== this.width.type) {
            return false;
          }
        }

        if (this.initialCardData.customSizeRatio.height) {
          if (this.initialCardData.customSizeRatio.height.value !== this.height.value) {
            return false;
          }

          if (this.initialCardData.customSizeRatio.height.type !== this.height.type) {
            return false;
          }
        }
      }

      return true;
    },

    slideHasNoChanges() {
      if (this.initialSlideData) {
        if (this.initialSlideData.showAsGraph !== this.selectedSlide.showAsGraph) {
          return false;
        }

        if (this.initialSlideData.showAgentNames !== this.selectedSlide.showAgentNames) return false;

        if (
          JSON.stringify(this.initialSlideData.graphThreshold) !== JSON.stringify(this.selectedSlide.graphThreshold)
        ) {
          return false;
        }
        if (this.initialSlideData.name !== this.selectedSlide.name) {
          return false;
        }
        if (
          JSON.stringify(this.initialSlideData.businessProcesses) !==
          JSON.stringify(this.selectedSlide.businessProcesses)
        ) {
          return false;
        }

        if (JSON.stringify(this.initialSlideData.states) !== JSON.stringify(this.selectedSlide.states)) {
          return false;
        }

        if (JSON.stringify(this.initialSlideData.callbacks) !== JSON.stringify(this.selectedSlide.callbacks)) {
          return false;
        }

        if (
          JSON.stringify(this.initialSlideData.interactionsTypeList) !==
          JSON.stringify(this.selectedSlide.interactionsTypeList)
        ) {
          return false;
        }

        if (JSON.stringify(this.initialSlideData.mediaTypes) !== JSON.stringify(this.selectedSlide.mediaTypes)) {
          return false;
        }
      }
      return true;
    },

    graphType() {
      if (
        this.localCardData.componentName !== 'EcsInteractionsSlide' ||
        !this.selectedSlide.interactionsTypeList ||
        this.selectedSlide.interactionsTypeList.length === 0
      )
        return '';
      return (
        ' ' +
        (this.selectedSlide.interactionsTypeList.length === 1
          ? this.$t('wallboard.graphHistogram')
          : this.$t('wallboard.graphPiechart'))
      );
    }
  },

  watch: {
    'selectedSlide.showAsGraph': function(newVal, oldVal) {
      if (newVal === true) this.selectedSlide.showAgentNames = false;
    },
    'selectedSlide.showAgentNames': function(newVal, oldVal) {
      if (newVal === true) this.selectedSlide.showAsGraph = false;
    }
  },

  methods: {
    ...mapActions('WALLBOARD', ['updateWallboardCards']),

    onDrop(dropResult) {
      const { removedIndex, addedIndex, payload } = dropResult;
      if ((!removedIndex && !addedIndex) || removedIndex === addedIndex) {
        return;
      }

      const slides = [...this.localCardData.slides];
      let removedElement = payload;

      if (removedIndex !== null) {
        removedElement = slides.splice(removedIndex, 1)[0];
      }

      if (addedIndex !== null) {
        slides.splice(addedIndex, 0, removedElement);
      }

      this.localCardData.slides = slides;
    },

    interactionsUpdated(interaction) {
      const INTERACTION_TRIGGER = 'Queued';

      this.selectedSlide.interactionsTypeList = this.selectedSlide.interactionsTypeList.filter(currentInteraction => {
        if (interaction === INTERACTION_TRIGGER) {
          return currentInteraction === INTERACTION_TRIGGER;
        }
        return currentInteraction !== INTERACTION_TRIGGER;
      });
    },

    toggleSelectAllBPs() {
      this.$nextTick(() => {
        if (!this.selectedSlide.businessProcesses) this.selectedSlide.businessProcesses = [];

        if (this.availableBusinessProcesses.length === this.selectedSlide.businessProcesses.length) {
          this.selectedSlide.businessProcesses = [];
        } else {
          this.selectedSlide.businessProcesses = this.availableBusinessProcessesIds.slice();
        }
      });
    },

    open(card) {
      this.localCardData = { ...card };
      this.initialCardData = { ...card };
      if (this.isSupervisor) {
        this.width = { ...this.initialCardData.customSizeRatio.width };
        this.height = { ...this.initialCardData.customSizeRatio.height };
      }

      this.isSupervisorCard = this.supervisorCards.includes(this.localCardData.componentName);
    },

    closeModal() {
      this.localCardData = null;
      this.initialCardData = null;
      this.discard();
    },

    discard() {
      this.tab = 0;
      this.selectedSlide = { businessProcesses: [] };
      this.initialSlideData = { businessProcesses: [] };
    },

    openSlideSettings(slide, isEditingMode = false) {
      this.tab = 1;
      this.isEditingMode = isEditingMode;
      this.selectedSlide = structuredClone(slide);
      this.initialSlideData = structuredClone(slide);
    },

    removeSlide(slideToRemove) {
      const newSlides = this.localCardData.slides.filter(slide => slide._id !== slideToRemove._id);

      this.updateSlides(newSlides);
    },

    editSlide() {
      const newSlides = this.localCardData.slides.map(slide => {
        if (slide._id === this.selectedSlide._id) {
          this.selectedSlide = this.preventEmptySlideName(this.selectedSlide);

          return this.selectedSlide;
        }

        return slide;
      });

      this.updateSlides(newSlides);
    },

    addSlide() {
      this.selectedSlide = this.preventEmptySlideName(this.selectedSlide);

      const newSlides = [...this.localCardData.slides, this.selectedSlide];

      this.updateSlides(newSlides);
    },

    updateSlides(newSlides) {
      this.localCardData = {
        ...this.localCardData,
        slides: newSlides
      };

      this.updateCard();
    },

    updateCard() {
      this.isLoading = true;

      const updatedWallboardCards = this.wallboardCards.map(card => {
        if (card._id === this.localCardData._id) {
          if (this.isSupervisor) {
            this.localCardData.customSizeRatio.width = this.width;
            this.localCardData.customSizeRatio.height = this.height;
          }
          return this.localCardData;
        }

        return card;
      });

      this.updateWallboardCards({ cards: updatedWallboardCards, dashboardId: this.dashboardId }).finally(() => {
        this.isLoading = false;

        this.discard();
        // Refresh values so Card has changes method properly work
        this.open(this.localCardData);
        this.$refs.saveSuccessful.classList.remove('visible');
        // Trigger reflow
        void this.$refs.saveSuccessful.offsetWidth;
        this.$refs.saveSuccessful.classList.add('visible');
      });
    },

    preventEmptySlideName(slide) {
      if (slide.name === '') {
        if (slide.businessProcesses.length === 1) {
          slide.name = this.availableBusinessProcesses.find(
            bp => bp.businessProcessId === slide.businessProcesses[0]
          ).businessProcessName;
        } else {
          slide.name = this.$t('wallboard.newSlide');
        }
      }

      return slide;
    }
  }
};
</script>

<style src="./scoped.css" scoped />
